import { useCallback, useEffect, useMemo, useState } from 'react';
import mabarService from '@/services/mabarService';
import communityService, { MabarEvent, MabarParticipant } from '@/services/communityService';
import { createPickleballService, type PickleballSessionListItem, type PickleballStandingItem } from '@/services/pickleballService';
import { flattenPickleballGames } from '@/components/community/pickleball/pickleballListUtils';
import { getApiErrorMessage } from '@/utils/apiErrorMessage';

export function usePickleballStatistik(slug: string | undefined, isClientView: boolean) {
  const service = useMemo(() => createPickleballService({ isClient: isClientView }), [isClientView]);
  const [event, setEvent] = useState<MabarEvent | null>(null);
  const [sessions, setSessions] = useState<PickleballSessionListItem[]>([]);
  const [standings, setStandings] = useState<PickleballStandingItem[]>([]);
  const [participants, setParticipants] = useState<MabarParticipant[]>([]);
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  const loadData = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    try {
      const [sessData, standData] = await Promise.all([
        service.listSessions(slug),
        service.getStandings(slug),
      ]);
      if (isClientView) {
        const ev = await mabarService.getEventBySlug(slug);
        setEvent(
          ev
            ? ({
                name: ev.name,
                event_date: ev.event_date,
                start_time: ev.start_time,
                end_time: ev.end_time,
                address: ev.address,
                lokasi_arena: ev.lokasi_arena,
                host: ev.host,
                sport_category: ev.sport_category,
              } as MabarEvent)
            : null
        );
        setParticipants([]);
      } else {
        const eventData = await communityService.getEventBySlug(slug);
        setEvent(eventData);
        setParticipants([]);
      }
      setSessions(sessData ?? []);
      setStandings(standData ?? []);
      try {
        const access = await service.getAccess(slug);
        setCanEdit(access.canEdit);
        if (access.canEdit && !isClientView) {
          const partRes = await communityService.getEventParticipants(slug, {
            status: 'confirmed',
            limit: 200,
          });
          setParticipants(partRes.participants ?? []);
        } else if (access.canEdit && isClientView) {
          const dash = await mabarService.getEventDashboard(slug, 1, 200);
          setParticipants(
            (dash.participants ?? [])
              .filter((p) => p.status === 'confirmed')
              .map((p) => ({
                id: p.id,
                event_id: 0,
                user_id: 0,
                name: p.name,
                role: p.role,
                price: p.price,
                email: p.email,
                wa_number: p.wa_number,
                status: 'confirmed' as const,
                joined_at: p.joined_at,
              }))
          );
        }
      } catch {
        setCanEdit(false);
      }
      setErrorMessage('');
    } catch (err: unknown) {
      setErrorMessage(getApiErrorMessage(err, 'Gagal memuat data'));
    } finally {
      setLoading(false);
    }
  }, [slug, isClientView, service]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return {
    event,
    sessions,
    standings,
    participants,
    canEdit,
    loading,
    errorMessage,
    flatGames: flattenPickleballGames(sessions),
    service,
    loadData,
  };
}
